import React, { ReactNode, SyntheticEvent } from 'react'
import styled, { css } from 'styled-components'
import CreationToolFileType from '../../../../types/file-type'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolUIAnimation from '../../../../types/ui-animation'
import { StickerBlock } from '../../../blocks'
import VideoPlayerComponent from '../../../video-player'
import { CreationToolUploadSource } from '../../types'

const MAX_WIDTH = 460
const MAX_HEIGHT = 520
const MAX_ASPECT_RATIO = MAX_WIDTH / MAX_HEIGHT
const TRANSPARENT_PIXEL =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='

const Preview = styled.img`
    border-radius: inherit;
    display: block;
    margin: 0 auto;
`

const VideoPlayer = styled(VideoPlayerComponent)`
    border-radius: inherit;
    margin: 0 auto;
`

const Container = styled(StickerBlock)<{ isAbsolute: boolean }>`
    ${CreationToolUIAnimation.css(CreationToolUIAnimation.FadeIn)}
    position: relative;

    ${({ isAbsolute }) =>
        isAbsolute &&
        css`
            box-sizing: border-box;
            padding: 15px;

            ${Preview} {
                height: 100%;
                left: 0;
                object-fit: cover;
                position: absolute;
                top: 0;
                width: 100%;
            }
        `}
`

const Dim = styled.div`
    background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.5) 0%,
        rgba(0, 0, 0, 0) 40%,
        rgba(0, 0, 0, 0) 60%,
        rgba(0, 0, 0, 0.5) 100%
    );
    border-radius: inherit;
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
`

const Watermark = styled.img`
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
`

type Props = {
    className?: string
    children?: ReactNode
    source: CreationToolUploadSource
    previewUrl?: string
}

const CreationUploadItemPreview = ({ className, source: uploadSource, previewUrl, children }: Props) => {
    const { error, source, outputType, edits } = uploadSource
    const isVideo = source.type === CreationToolFileType.VIDEO || source.type === CreationToolFileType.VIDEO_EMBED
    const aspectRatio = edits?.crop ? edits.crop.width / edits.crop.height : source.width / source.height
    const width = aspectRatio < MAX_ASPECT_RATIO ? Math.round(MAX_HEIGHT * aspectRatio) : MAX_WIDTH
    const containerStyles =
        !!error || outputType === CreationToolOutputType.Sticker ? {} : { background: 'transparent' }

    const onError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.src = TRANSPARENT_PIXEL
    }

    if (!previewUrl) return null

    if (!error && isVideo) {
        return (
            <Container className={className} isAbsolute={false} style={containerStyles}>
                <VideoPlayer
                    {...{
                        source,
                        previewUrl,
                        width,
                        startTime: edits?.trim?.start,
                        endTime: edits?.trim?.end,
                        crop: edits?.crop,
                        hideButtons: outputType !== CreationToolOutputType.Video,
                    }}
                >
                    <>
                        {!!children && <Dim />}
                        {edits?.watermarkUrl && <Watermark src={edits.watermarkUrl} alt="" />}
                        {children}
                    </>
                </VideoPlayer>
            </Container>
        )
    }

    if (isVideo) {
        return (
            <Container className={className} isAbsolute={!!error} style={containerStyles}>
                <Preview as="video" src={previewUrl} style={{ width }} muted autoPlay loop />
                {!!children && <Dim />}
                {children}
            </Container>
        )
    }

    return (
        <Container className={className} isAbsolute={!!error} style={containerStyles}>
            <Preview src={previewUrl} style={{ width }} onError={onError} />
            {!!children && <Dim />}
            {children}
        </Container>
    )
}

export default CreationUploadItemPreview
